import { useMutation, useQuery } from "@tanstack/react-query";
import {
  CreateExpenseSupplierFormValues,
  UpdateExpenseSupplierFormValues,
  createExpenseSupplier,
  deleteExpenseSupplier,
  getExpenseSuppliers,
  restoreExpenseSupplier,
  updateExpenseSupplier,
} from "../api/expenseSuppliers";
import { queryClient } from "@/api/queryClient";
import { Supplier } from "@/types/supplier";
import { ApiResponse, PaginatedApiResponse, PaginationInfo } from "@/types/api";
import { AxiosRequestConfig } from "axios";

interface useExpenseSupplierMutationOptions<T> {
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
}

export const useGetExpenseSuppliers = (config?: AxiosRequestConfig) => {
  return useQuery<
    PaginatedApiResponse<Supplier[]>,
    Error,
    { data: Supplier[]; pagination: PaginationInfo }
  >({
    queryKey: ["expense-suppliers", config?.params],
    queryFn: () => getExpenseSuppliers(config),
    select: (response) => ({
      data: response.data,
      pagination: response.pagination,
    }),
  });
};

export const useGetExpenseSuppliersOptions = (config?: AxiosRequestConfig) => {
  return useQuery<ApiResponse<Supplier[]>, Error, Supplier[]>({
    queryKey: ["expense-suppliers-options", config?.params],
    queryFn: () => getExpenseSuppliers(config),
    select: (response) => response.data,
  });
};

export const useCreateExpenseSupplier = (
  options?: useExpenseSupplierMutationOptions<Supplier>
) => {
  return useMutation({
    mutationFn: (data: CreateExpenseSupplierFormValues) =>
      createExpenseSupplier(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-suppliers"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};

export const useUpdateExpenseSupplier = (
  options?: useExpenseSupplierMutationOptions<Supplier>
) => {
  return useMutation({
    mutationFn: (data: UpdateExpenseSupplierFormValues) =>
      updateExpenseSupplier(data.id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-suppliers"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};

export const useDeleteExpenseSupplier = (
  options?: useExpenseSupplierMutationOptions<Supplier>
) => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteExpenseSupplier(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-suppliers"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};

export const useRestoreExpenseSupplier = (
  options?: useExpenseSupplierMutationOptions<Supplier>
) => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) => restoreExpenseSupplier(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-suppliers"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};
